import { useState, type MouseEvent } from 'react';
import { Box, Button, Divider, Menu, MenuItem } from '@mui/material';
import ExpandDownIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/ExpandDown.js';
import { useTranslation } from 'react-i18next';
import { useOnboardingLens } from './onboardingLens';

const SKIP_SETUP_MENU_OPTIONS = [
  { key: 'onboarding.wizard.step1.skip_do_later', action: 'do_later' as const },
  { key: 'onboarding.wizard.step1.skip_assign_to_colleague', action: 'not_my_task' as const },
] as const;

interface OnboardingSkipSetupDropdownProps {
  onDoLater: () => void;
  onNotMyTask: () => void;
  disabled?: boolean;
  /** Disabled when reassign-candidates returns no colleagues (solo org). */
  assignToColleagueDisabled?: boolean;
}

/** Figma node 16692:5495 — tertiary button dropdown for Step 1 footer. */
export function OnboardingSkipSetupDropdown({
  onDoLater,
  onNotMyTask,
  disabled = false,
  assignToColleagueDisabled = false,
}: OnboardingSkipSetupDropdownProps) {
  const { t } = useTranslation();
  const { layout, typography, colors, outlineDefault } = useOnboardingLens();
  const { footer } = layout;
  const step1Footer = layout.welcomeStep1.footer;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: 'do_later' | 'not_my_task') => {
    handleClose();
    if (action === 'do_later') {
      onDoLater();
      return;
    }
    onNotMyTask();
  };

  const menuItemSx = {
    py: layout.formField.inputPaddingY,
    px: layout.formField.inputPaddingX,
    color: colors.typeDefault,
    ...typography.body,
  };

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? 'onboarding-skip-setup-menu' : undefined}
        endIcon={
          <Box
            component="span"
            sx={{
              display: 'flex',
              width: layout.icon.md,
              height: layout.icon.md,
              alignItems: 'center',
            }}
          >
            <ExpandDownIcon />
          </Box>
        }
        sx={{
          textTransform: 'none',
          flexShrink: 0,
          height: step1Footer.buttonHeight,
          minHeight: step1Footer.buttonHeight,
          minWidth: step1Footer.skipButtonMinWidth,
          px: step1Footer.buttonPaddingX,
          py: step1Footer.buttonPaddingY,
          borderRadius: step1Footer.buttonRadius,
          color: colors.typeDefault,
          borderColor: outlineDefault,
          ...typography.button,
          outline: 'none',
          '&:hover': {
            borderColor: outlineDefault,
            backgroundColor: 'transparent',
          },
          '&:focus': { outline: 'none' },
          '&:focus:not(:focus-visible)': { boxShadow: 'none' },
          '&.Mui-focusVisible': { outline: 'none', boxShadow: colors.focusRing },
        }}
      >
        {t('onboarding.wizard.step1.skip_setup')}
      </Button>

      <Menu
        id="onboarding-skip-setup-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: footer.menuMinWidth,
              borderRadius: footer.menuBorderRadius,
              boxShadow: footer.menuShadow,
              mt: footer.menuOffset,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('do_later')} sx={menuItemSx}>
          {t(SKIP_SETUP_MENU_OPTIONS[0].key)}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleMenuItemClick('not_my_task')}
          disabled={assignToColleagueDisabled}
          sx={menuItemSx}
        >
          {t(SKIP_SETUP_MENU_OPTIONS[1].key)}
        </MenuItem>
      </Menu>
    </>
  );
}
